import IconButton from '@/global/components/Button/IconButton'
import { optionsColumn } from '@/global/components/DataGrid/hooks/useDataGridPro'
import { GridCellEditCommitParams } from '@/global/components/DataGrid/utils/types'
import IconeAjuda from '@/global/components/IconeAjuda'
import { SelectEmpresa } from '@/global/components/Selects'
import ContentGuide from '@/global/guides/ContentGuide'
import TabbedDialog from '@/global/guides/Dialog/TabbedDialog'
import { hasPermission } from '@/hooks/useBackendMenu'
import { useRubricasOutletContext } from '@/pages/Rubricas.context'
import {
    centroCusto as centroCusto_api,
    empresas as empresas_api,
    funcionario as funcionario_api,
    local as local_api,
    rubrica as rubricas_api,
} from '@/service/api'
import { invariant } from '@acme/core'
import { Link2Icon, LinkIcon, Trash2Icon } from 'lucide-react'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { useStore } from 'store'

type TipoExcecao = 'empresa' | 'local' | 'centroCusto' | 'funcionario'

export default function ExcecoesRubricas() {
    const [tipo, setTipo] = useState<TipoExcecao>('empresa')
    const companyId = useStore(state => state.companyId)

    const { data: empresas = [], isLoading: loadingEmpresas } =
        empresas_api.useFetch()
    const { data: locais = [], isLoading: loadingLocais } = local_api.useFetch({
        queryParams: companyId ? { empresaId: companyId } : undefined,
        enabled: tipo === 'local' && !!companyId,
    })
    const { data: centrosCusto = [], isLoading: loadingCC } =
        centroCusto_api.useFetch({
            queryParams: companyId ? { empresaId: companyId } : undefined,
            enabled: tipo === 'centroCusto' && !!companyId,
        })
    const { data: funcionarios = [], isLoading: loadingFuncionarios } =
        funcionario_api.useFetch({
            queryParams: companyId ? { empresaId: companyId } : undefined,
            enabled: tipo === 'funcionario' && !!companyId,
        })

    const { data } = useRubricasOutletContext()
    const { id } = useParams()

    type ExcecaoOrigem = TipoExcecao | 'padrao'

    function getExcecaoVigente(t: TipoExcecao, row: any) {
        const excecaoEmpresa = rubricaAtual.ExcecaoRubrica?.find(
            e => e.empresaId === row.empresaId,
        )

        if (t === 'empresa') {
            return {
                excecao: excecaoEmpresa,
                origem: excecaoEmpresa
                    ? ('empresa' as const)
                    : ('padrao' as const),
            }
        }

        if (t === 'local') {
            const excecaoLocal = rubricaAtual.ExcecaoRubrica?.find(
                e => e.localId === row.id,
            )

            return {
                excecao: excecaoLocal ?? excecaoEmpresa,
                origem: excecaoLocal
                    ? ('local' as const)
                    : excecaoEmpresa
                      ? ('empresa' as const)
                      : ('padrao' as const),
            }
        }

        if (t === 'centroCusto') {
            const excecaoCentroCusto = rubricaAtual.ExcecaoRubrica?.find(
                e => e.centroCustoId === row.id,
            )

            return {
                excecao: excecaoCentroCusto ?? excecaoEmpresa,
                origem: excecaoCentroCusto
                    ? ('centroCusto' as const)
                    : excecaoEmpresa
                      ? ('empresa' as const)
                      : ('padrao' as const),
            }
        }

        const excecaoFuncionario = rubricaAtual.ExcecaoRubrica?.find(
            e => e.funcionarioId === row.id,
        )
        const excecaoLocal = rubricaAtual.ExcecaoRubrica?.find(e =>
            row.FuncionarioLocais?.some(
                (local: any) => local.localId === e.localId,
            ),
        )
        const excecaoCentroCusto = rubricaAtual.ExcecaoRubrica?.find(e =>
            row.FuncionarioCentroCusto?.some(
                (centro: any) => centro.id === e.centroCustoId,
            ),
        )

        return {
            excecao:
                excecaoFuncionario ??
                excecaoLocal ??
                excecaoCentroCusto ??
                excecaoEmpresa,
            origem: excecaoFuncionario
                ? ('funcionario' as const)
                : excecaoLocal
                  ? ('local' as const)
                  : excecaoCentroCusto
                    ? ('centroCusto' as const)
                    : excecaoEmpresa
                      ? ('empresa' as const)
                      : ('padrao' as const),
        }
    }

    const { mutate: upsert } = rubricas_api.excecao.mutations.useCreate({
        onError: (e: any) => {
            toast.error(e.message)
        },
        invalidateQuery: true,
    })
    const { mutate: deleteExcecao } = rubricas_api.excecao.mutations.useDelete({
        onSuccess: async () => {
            await rubricas_api.invalidateQueries()
            toast.success('Exceção excluída com sucesso')
        },
        onError: (e: any) => {
            toast.error(e.message)
        },
        invalidateQuery: true,
    })

    if (!id) return null
    const rubrica = data.find(r => r.id === Number(id))
    if (!rubrica) return null
    const rubricaAtual = rubrica!

    function buildRows(t: TipoExcecao) {
        invariant(!!rubricaAtual, 'Rubrica não encontrada')

        if (t === 'empresa') {
            return empresas
                .map(empresa => {
                    const { excecao, origem } = getExcecaoVigente('empresa', {
                        ...empresa,
                        empresaId: empresa.id,
                    })
                    return {
                        id: `empresa-${empresa.id}`,
                        excecaoId: excecao?.id ?? null,
                        rubricaId: rubrica.id,
                        empresaId: empresa.id,
                        codigo: excecao ? excecao.codigo : rubrica.codigo,
                        label: empresa.nomeFantasia,
                        existeExcecao: !!excecao && origem === 'empresa',
                        existeExcecaoOutroTipo:
                            !!excecao && origem !== 'empresa',
                        origemExcecao: origem,
                    }
                })
                .sort((a, b) => a.label.localeCompare(b.label))
        }

        if (t === 'local') {
            return locais
                .map((l: any) => {
                    const { excecao, origem } = getExcecaoVigente('local', {
                        ...l,
                        empresaId: l.empresaId ?? companyId,
                    })
                    return {
                        id: `local-${l.id}`,
                        excecaoId: excecao?.id ?? null,
                        rubricaId: rubrica.id,
                        localId: l.id,
                        empresaId: l.empresaId ?? companyId,
                        codigo: excecao ? excecao.codigo : rubrica.codigo,
                        label: l.nomeLocal,
                        existeExcecao: !!excecao && origem === 'local',
                        existeExcecaoOutroTipo: !!excecao && origem !== 'local',
                        origemExcecao: origem,
                    }
                })
                .sort((a, b) => a.label.localeCompare(b.label))
        }

        if (t === 'centroCusto') {
            return centrosCusto
                .map((cc: any) => {
                    const { excecao, origem } = getExcecaoVigente(
                        'centroCusto',
                        {
                            ...cc,
                            empresaId: cc.empresaId ?? companyId,
                        },
                    )
                    return {
                        id: `cc-${cc.id}`,
                        excecaoId: excecao?.id ?? null,
                        rubricaId: rubrica.id,
                        centroCustoId: cc.id,
                        empresaId: cc.empresaId ?? companyId,
                        codigo: excecao ? excecao.codigo : rubrica.codigo,
                        label: cc.nomeCentroCusto,
                        existeExcecao: !!excecao && origem === 'centroCusto',
                        existeExcecaoOutroTipo:
                            !!excecao && origem !== 'centroCusto',
                        origemExcecao: origem,
                    }
                })
                .sort((a, b) => a.label.localeCompare(b.label))
        }

        return funcionarios
            .map((f: any) => {
                const { excecao, origem } = getExcecaoVigente('funcionario', {
                    ...f,
                    empresaId: f.empresaId ?? companyId,
                })
                return {
                    id: `func-${f.id}`,
                    excecaoId: excecao?.id ?? null,
                    rubricaId: rubrica.id,
                    funcionarioId: f.id,
                    empresaId: f.empresaId ?? companyId,
                    codigo: excecao ? excecao.codigo : rubrica.codigo,
                    label: f.nome,
                    existeExcecao: !!excecao && origem === 'funcionario',
                    existeExcecaoOutroTipo:
                        !!excecao && origem !== 'funcionario',
                    origemExcecao: origem,
                }
            })
            .sort((a, b) => a.label.localeCompare(b.label))
    }

    function handleCellEditCommit({
        field,
        value,
        row,
    }: GridCellEditCommitParams) {
        invariant(!!rubricaAtual, 'Rubrica não encontrada')
        if (!value) return toast.warning('O valor não foi salvo')

        if (field === 'codigo') {
            if (Number(value) === rubricaAtual.codigo) {
                if (row.excecaoId) return deleteExcecao(row.excecaoId as any)
                return
            }

            const payload: Record<string, any> = {
                rubricaId: rubricaAtual.id,
                codigo: Number(value),
            }
            if (tipo === 'empresa') payload.empresaId = row.empresaId
            else if (tipo === 'local') payload.localId = row.localId
            else if (tipo === 'centroCusto')
                payload.centroCustoId = row.centroCustoId
            else payload.funcionarioId = row.funcionarioId

            return upsert(payload as any, {
                onSuccess: async () => {
                    await rubricas_api.invalidateQueries()
                    toast.success('Código atualizado com sucesso')
                },
            })
        }
    }

    const optionsColumn_ = {
        ...optionsColumn,
        headerAlign: 'center' as const,
        field: '__excluir',
        renderCell: ({
            row: {
                excecaoId,
                existeExcecao,
                existeExcecaoOutroTipo,
                origemExcecao,
            },
        }: {
            row: {
                excecaoId: number | null
                existeExcecao: boolean
                existeExcecaoOutroTipo?: boolean
                origemExcecao?: ExcecaoOrigem
            }
        }) =>
            existeExcecao ? (
                <IconButton
                    title="Excluir exceção e utilizar código padrão"
                    onClick={() => deleteExcecao(excecaoId as any)}
                >
                    <Trash2Icon className="text-error" />
                </IconButton>
            ) : existeExcecaoOutroTipo ? (
                <IconeAjuda Icon={<Link2Icon className="text-warning" />}>
                    Código vindo de exceção de{' '}
                    {origemExcecao === 'padrao' ? 'outro tipo' : origemExcecao}
                </IconeAjuda>
            ) : (
                <IconButton disabled title="Utiliza o código padrão">
                    <LinkIcon />
                </IconButton>
            ),
        renderHeader: () => (
            <div className="pt-2">
                <IconeAjuda>
                    Clique sobre o campo código para editar ou criar uma
                    exceção.
                    <br />
                    <br />O ícone <LinkIcon className="inline size-4" /> indica
                    que o item está utilizando o código padrão da rubrica.
                    <br />O ícone{' '}
                    <Link2Icon className="inline text-warning size-4" /> indica
                    que o código vem de uma exceção de outro tipo.
                    <br /> Já o ícone{' '}
                    <Trash2Icon className="inline text-error size-4" /> indica
                    que existe uma exceção que pode ser excluída.
                </IconeAjuda>
            </div>
        ),
    }

    function makeDataGrid(
        t: TipoExcecao,
        headerName: string,
        isLoading: boolean,
    ) {
        return (
            <ContentGuide.DataGrid
                loading={isLoading}
                onCellEditCommit={handleCellEditCommit}
                columns={[
                    {
                        field: 'codigo',
                        headerName: 'Código',
                        width: 150,
                        editable: hasPermission(['update', 'Rubricas']),
                        type: 'number',
                    },
                    {
                        field: 'label',
                        headerName,
                        flex: 1,
                        maxWidth: 350,
                    },
                    optionsColumn_,
                ]}
                rows={companyId || t === 'empresa' ? buildRows(t) : []}
                pageSize={Infinity}
                hideFooter
                ToolbarProps={{
                    showQuickFilter: true,
                    hideFilterButton: true,
                }}
                className="min-h-full"
                headerHeight={40}
            />
        )
    }

    const empresaSelector = <SelectEmpresa variant="form" />

    const tabs = [
        {
            name: 'empresa',
            label: 'Empresa',
            content: makeDataGrid('empresa', 'Empresa', loadingEmpresas),
        },
        {
            name: 'local',
            label: 'Local',
            content: (
                <div className="flex flex-col gap-4 h-[calc(100%-4rem)]">
                    {empresaSelector}
                    {makeDataGrid('local', 'Local', loadingLocais)}
                </div>
            ),
        },
        {
            name: 'centroCusto',
            label: 'Centro de custo',
            content: (
                <div className="flex flex-col gap-4 h-[calc(100%-4rem)]">
                    {empresaSelector}
                    {makeDataGrid('centroCusto', 'Centro de custo', loadingCC)}
                </div>
            ),
        },
        {
            name: 'funcionario',
            label: 'Funcionário',
            content: (
                <div className="flex flex-col gap-4 h-[calc(100%-4rem)]">
                    {empresaSelector}
                    {makeDataGrid(
                        'funcionario',
                        'Funcionário',
                        loadingFuncionarios,
                    )}
                </div>
            ),
        },
    ]

    return (
        <TabbedDialog.Root
            tabs={tabs}
            onTabChange={v => setTipo(v as TipoExcecao)}
        >
            <TabbedDialog.Title action="Gerenciar exceções da rubrica">
                {rubrica.referencia}
            </TabbedDialog.Title>
            <TabbedDialog.Content />
            <TabbedDialog.Actions>
                <TabbedDialog.Actions.SecondaryButton />
            </TabbedDialog.Actions>
        </TabbedDialog.Root>
    )
}
